/**
 * Color utility functions for DataTable cell renderers
 *
 * This module provides reusable color schemes for badges, backgrounds,
 * and text colors based on various data values.
 * Colors are defined as CSS variables in custom.css with dark mode overrides.
 */

export interface ColorScheme {
  background: string;
  text: string;
}

/**
 * Get background and text colors for activity types
 */
export const getActivityTypeColors = (activityType: string | undefined | null): ColorScheme => {
  if (!activityType) {
    return { background: 'var(--badge-unknown-bg)', text: 'var(--badge-unknown-text)' };
  }

  const normalized = activityType.trim();

  switch (normalized) {
    case 'Minecraft':
      return { background: 'var(--badge-minecraft-bg)', text: 'var(--badge-minecraft-text)' };
    case 'Roblox':
      return { background: 'var(--badge-roblox-bg)', text: 'var(--badge-roblox-text)' };
    case 'Python':
      return { background: 'var(--badge-python-bg)', text: 'var(--badge-python-text)' };
    case 'Scratch':
      return { background: 'var(--badge-scratch-bg)', text: 'var(--badge-scratch-text)' };
    case 'Web-Dev':
      return { background: 'var(--badge-webdev-bg)', text: 'var(--badge-webdev-text)' };
    case 'Code-In-Class':
      return { background: 'var(--badge-code-in-class-bg)', text: 'var(--badge-code-in-class-text)' };
    case 'Sphero':
      return { background: 'var(--badge-sphero-bg)', text: 'var(--badge-sphero-text)' };
    case 'Canceled':
      return { background: 'var(--badge-canceled-bg)', text: 'var(--badge-canceled-text)' };
    default:
      return { background: 'var(--badge-unknown-bg)', text: 'var(--badge-unknown-text)' };
  }
};

/**
 * Get background and text colors for device types
 */
export const getDeviceColors = (device: string | undefined | null): ColorScheme => {
  if (!device) {
    return { background: 'var(--badge-host-unknown-bg)', text: 'var(--badge-host-unknown-text)' };
  }

  const normalized = device.trim();

  switch (normalized) {
    case 'BYOD':
      return { background: 'var(--badge-byod-bg)', text: 'var(--badge-byod-text)' };
    case 'Customer Provided':
      return { background: 'var(--badge-customer-provided-bg)', text: 'var(--badge-customer-provided-text)' };
    case 'ASG Tablets':
      return { background: 'var(--badge-asg-tablets-bg)', text: 'var(--badge-asg-tablets-text)' };
    case 'Unknown':
      return { background: 'var(--badge-host-unknown-bg)', text: 'var(--badge-host-unknown-text)' };
    default:
      return { background: 'var(--badge-host-unknown-bg)', text: 'var(--badge-host-unknown-text)' };
  }
};

/**
 * Get background and text colors for host values
 */
export const getHostColors = (host: string | undefined | null): ColorScheme => {
  if (!host) {
    return { background: 'var(--badge-host-unknown-bg)', text: 'var(--badge-host-unknown-text)' };
  }

  if (host.toLowerCase().includes('unknown')) {
    return { background: 'var(--badge-host-unknown-bg)', text: 'var(--badge-host-unknown-text)' };
  }

  return { background: 'var(--badge-host-known-bg)', text: 'var(--badge-host-known-text)' };
};

/**
 * Get background and text colors for customer types
 */
export const getCustomerTypeColors = (type: string | undefined | null): ColorScheme => {
  switch (type) {
    case 'New':
      return { background: 'var(--badge-customer-new-bg)', text: 'var(--badge-customer-new-text)' };
    case 'Repeat':
      return { background: 'var(--badge-customer-repeat-bg)', text: 'var(--badge-customer-repeat-text)' };
    default:
      return { background: 'var(--badge-unknown-bg)', text: 'var(--badge-unknown-text)' };
  }
};

/**
 * Get Bootstrap badge variant for format values
 */
export const getFormatBadgeVariant = (format: string | undefined | null): string => {
  switch (format) {
    case 'Virtual':
      return 'success';
    case 'Hybrid':
      return 'warning';
    case 'In-Person':
      return 'primary';
    default:
      return 'secondary';
  }
};

/**
 * Known activity types that should have fixed width badges
 */
export const KNOWN_ACTIVITY_TYPES = [
  'Minecraft',
  'Roblox',
  'Python',
  'Scratch',
  'Web-Dev',
  'Code-In-Class',
  'Sphero',
  'Canceled'
];

/**
 * Check if an activity type is a known type (for badge sizing)
 */
export const isKnownActivityType = (activityType: string | undefined | null): boolean => {
  if (!activityType) return false;
  return KNOWN_ACTIVITY_TYPES.includes(activityType.trim());
};
